import React, { FC, useEffect, useState, useCallback, useRef } from 'react';
import { Box, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import CommonMap from '../../components/map/CommonMap';
import { BoundaryApprovalSidebar } from './BoundaryApprovalSidebar';
import { BoundaryApprovalZonesLayer } from './BoundaryApprovalZonesLayer';
import { DefectiveGeometriesLayer } from '../../components/map/layers/DefectiveGeometriesLayer';
import { BoundaryApprovalDataProvider } from '../BoundaryApprovalDataProvider';
import { BoundaryApprovalJob } from '../domain/BoundaryApprovalJob';
import { BoundaryApprovalMissionManager } from '../domain/BoundaryApprovalMissionManager';
import { BoundaryApprovalService } from '../services/BoundaryApprovalService';
import { getCurrentMission, getCurrentSession } from '../../dataModelHelpers';
import { alertError, alertWarn, alertConfirm } from '../../alertDispatcher';
import { OnLoading, SettingsAccessLevel } from '../../types/types';
import { MAP_TYPES } from '../../components/map/helpers/mapTypes';
import EventBus from '../../EventBus';
import { MISSION_EVENTS } from '../../missionEvents';
import { Extent, getCenter } from 'ol/extent';
import logger from '../../logger';
import { dispatchSetCenter, dispatchSetExtent } from '../../mapEvents';
import { convertProjection4329 } from '../../utils';
import { IMissionLoader } from '../../services/mission-loading/MissionLoaderCommon';
import ExeMissionLoader from '../../services/mission-loading/original/ExeMissionLoader';
import { Session } from '../../db';
import selectionManager from '../../services/SelectionManager';
import type { Feature } from 'geojson';
import { fromLonLat } from 'ol/proj';
import LayerCheckContainer from '../../components/map/buttons/LayerCheckContainer';
import LayerCustomCheckBox from '../../components/map/buttons/LayerCustomCheck';
import { HistoricalLayersServiceImpl } from '../../services/historical-layers/HistoricalLayersService';

interface BoundaryApprovalTaskProps {
  accessLevel: SettingsAccessLevel;
  onLoading: OnLoading;
  missionActive: string;
}

export const BoundaryApprovalTask: FC<BoundaryApprovalTaskProps> = ({ accessLevel, onLoading, missionActive }) => {
  const [jobs, setJobs] = useState<BoundaryApprovalJob[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingJobId, setPendingJobId] = useState<string | null>(null);
  const [highlightedJobId, setHighlightedJobId] = useState<string | null>(null);
  const [defectiveGeometries, setDefectiveGeometries] = useState<Feature<GeoJSON.Point | GeoJSON.Polygon>[]>([]);
  const [warningGeometries, setWarningGeometries] = useState<Feature<GeoJSON.Point | GeoJSON.Polygon>[]>([]);
  const [showDefectiveGeometries, setShowDefectiveGeometries] = useState(true);
  const [showTimeline, setShowTimeline] = useState(false);

  const dataProvider = useRef(new BoundaryApprovalDataProvider());
  const missionManager = useRef(new BoundaryApprovalMissionManager());
  const approvalService = useRef(new BoundaryApprovalService());
  const historicalLayersService = useRef(new HistoricalLayersServiceImpl());

  // Close current mission if any
  useEffect(() => {
    Session.closeCurrentMission();
  }, []);

  // Load jobs from Airtable
  const loadJobs = useCallback(async () => {
    setIsLoading(true);

    try {
      const jobRecords = await dataProvider.current.fetchJobs();
      const boundaryJobs = jobRecords.map((record) => new BoundaryApprovalJob(record));

      missionManager.current.setJobs(boundaryJobs);
      setJobs(boundaryJobs);

      await logger.log('BOUNDARY_APPROVAL_JOBS_LOADED', {
        count: boundaryJobs.length,
      });
    } catch (error) {
      console.error('Failed to load jobs:', error);
      alertError('Failed to load jobs from Airtable');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle job click (activate)
  const handleJobClick = useCallback(
    async (jobId: string) => {
      const result = await missionManager.current.activateJob(jobId);

      if (!result.success) {
        if (result.needsConfirmation) {
          // Show confirmation dialog
          setPendingJobId(jobId);
          setConfirmDialogOpen(true);
        } else if (result.message) {
          alertWarn(result.message);
        }

        return;
      }

      // Load the mission for this job
      const job = missionManager.current.getJob(jobId);

      if (!job) {
        missionManager.current.deactivateCurrentJob();

        return;
      }

      await onLoading(async () => {
        const session = getCurrentSession();

        if (!session) {
          missionManager.current.deactivateCurrentJob();
          alertError('No active session');

          return;
        }

        try {
          // Clear any stale selection from the previous mission. The
          // shared SelectionManager is a singleton, and nothing else
          // drops its refs when `closeCurrentMission` blows away the
          // mission's OL features — leaving a dangling ref that breaks
          // identity-based guards on subsequent clicks.
          selectionManager.clearAll();

          session.closeCurrentMission();

          // Load the mission
          const missionLoader: IMissionLoader = new ExeMissionLoader();
          logger.log(`BoundaryApprovalTask: using the exe mission loader to load job ${jobId}`);

          const loadedMission = await session.loadMission(missionLoader, jobId, false);

          if (!loadedMission) {
            missionManager.current.deactivateCurrentJob();
          }

          // Center map on the job's boundary
          handleJobFocus(jobId);

          // Update jobs state to trigger re-render
          setJobs([...missionManager.current.getJobs()]);
        } catch (error) {
          console.error('Failed to load mission:', error);
          alertError(`Failed to load mission for job ${job.name}`);
        }
      });
    },
    [onLoading],
  );

  // Handle confirmation dialog
  const handleConfirmSwitch = useCallback(async () => {
    setConfirmDialogOpen(false);

    if (!pendingJobId) {
      return;
    }

    missionManager.current.deactivateCurrentJob();

    await handleJobClick(pendingJobId);
    setPendingJobId(null);
  }, [pendingJobId, handleJobClick]);

  const handleCancelSwitch = useCallback(() => {
    setConfirmDialogOpen(false);
    setPendingJobId(null);
  }, []);

  // Handle job focus (center map on job boundary)
  const handleJobFocus = useCallback((jobId: string) => {
    const job = missionManager.current.getJob(jobId);

    if (!job) {
      return;
    }

    // Center map on the job's boundary
    const bounds = job.getBounds();

    if (bounds) {
      // Convert bounds from lat/lon (EPSG:4326) to Web Mercator (EPSG:3857)
      // bounds are [lon, lat] pairs, where coord[0] = lon (x), coord[1] = lat (y)
      const convertedBounds = bounds.map((coord) => convertProjection4329(coord));
      const xs = convertedBounds.map((coord) => coord[0]);
      const ys = convertedBounds.map((coord) => coord[1]);
      const extent: Extent = [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];

      const center = getCenter(extent);
      dispatchSetExtent(extent);
      dispatchSetCenter(center);
    }

    // Trigger highlight effect (purple background for 3 seconds)
    setHighlightedJobId(jobId);
    setTimeout(() => {
      setHighlightedJobId(null);
    }, 3000);
  }, []);

  // Handle job visibility toggle
  const handleVisibilityToggle = useCallback((jobId: string) => {
    const job = missionManager.current.getJob(jobId);

    if (job) {
      job.setVisible(!job.isVisible);
      setJobs([...missionManager.current.getJobs()]);
    }
  }, []);

  // Handle approve
  const handleApprove = useCallback(
    async (jobId: string) => {
      const job = missionManager.current.getJob(jobId);

      if (!job) {
        return;
      }

      const mission = getCurrentMission();

      if (!mission || mission.job_id !== jobId) {
        alertError('Mission not loaded or mismatch');

        return;
      }

      // Validate mission
      const validation = approvalService.current.validateMissionForApproval(mission);

      for (const error of validation.errors) {
        alertWarn(`Found mission problem: ${error}`);
      }

      setWarningGeometries(validation.warningGeometries || []);

      if (!validation.valid) {
        alertError(`Cannot approve`);

        // Display the defective geometries on the map if available
        const allGeometries = [...(validation.defectiveGeometries || []), ...(validation.warningGeometries || [])];

        if (allGeometries.length > 0) {
          setDefectiveGeometries(validation.defectiveGeometries || []);

          // Focus the map on the error locations - calculate extent that includes all errors
          const pointCoordinates = allGeometries
            .filter((g) => g.geometry.type === 'Point')
            .map((g) => {
              const coords = g.geometry.coordinates as [number, number];

              return fromLonLat(coords);
            });

          if (pointCoordinates.length > 0) {
            // Calculate center as average of all error points
            const centerX = pointCoordinates.reduce((sum, coord) => sum + coord[0], 0) / pointCoordinates.length;
            const centerY = pointCoordinates.reduce((sum, coord) => sum + coord[1], 0) / pointCoordinates.length;
            dispatchSetCenter([centerX, centerY]);

            // Calculate extent that includes all error points with a buffer
            const buffer = 100; // Buffer in map units
            const minX = Math.min(...pointCoordinates.map((c) => c[0])) - buffer;
            const maxX = Math.max(...pointCoordinates.map((c) => c[0])) + buffer;
            const minY = Math.min(...pointCoordinates.map((c) => c[1])) - buffer;
            const maxY = Math.max(...pointCoordinates.map((c) => c[1])) + buffer;

            const extent: Extent = [minX, minY, maxX, maxY];
            dispatchSetExtent(extent);
          }
        }

        return;
      }

      // Clear any previous error geometries
      setDefectiveGeometries([]);
      setWarningGeometries([]);

      // Confirm approval
      const confirmed = await alertConfirm(`Are you sure you want to approve the boundary for job "${job.name}"?`);

      if (!confirmed) {
        return;
      }

      await onLoading(async () => {
        const success = await approvalService.current.approveJobBoundary(job.jobRecord, mission);

        if (success) {
          job.markAsApproved();
          setJobs([...missionManager.current.getJobs()]);

          // Deactivate the job
          missionManager.current.deactivateCurrentJob();
        }
      });
    },
    [onLoading],
  );

  // Preload historical layers for a mission
  const preloadHistoricalLayers = useCallback(async () => {
    const mission = getCurrentMission();

    if (!mission) {
      return;
    }

    await mission.loadHistoricalMapLayers(historicalLayersService.current);
  }, []);

  // Track mission changes to set hasUnsavedEdits
  useEffect(() => {
    const handleMissionBoundaryUpdated = () => {
      const activeJob = missionManager.current.getActiveJob();

      if (activeJob) {
        activeJob.setHasUnsavedEdits(true);
        setJobs([...missionManager.current.getJobs()]);

        // Recalculate defective geometries after boundary changes
        const mission = getCurrentMission();

        if (mission && mission.job_id === activeJob.id) {
          const validation = approvalService.current.validateMissionForApproval(mission);
          setDefectiveGeometries(validation.defectiveGeometries || []);
          setWarningGeometries(validation.warningGeometries || []);
        }
      }
    };

    EventBus.on(MISSION_EVENTS.BOUNDARY_UPDATED, handleMissionBoundaryUpdated);

    return () => {
      EventBus.remove(MISSION_EVENTS.BOUNDARY_UPDATED, handleMissionBoundaryUpdated);
    };
  }, []);

  // Subscribe to mission created/deleted events to preload historical layers
  useEffect(() => {
    EventBus.on(MISSION_EVENTS.CREATED_OR_DELETED, preloadHistoricalLayers);

    return () => {
      EventBus.remove(MISSION_EVENTS.CREATED_OR_DELETED, preloadHistoricalLayers);
    };
  }, [preloadHistoricalLayers]);

  // Initial load
  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  const activeJob = missionManager.current.getActiveJob();

  return (
    <Box sx={{ display: 'flex', width: '100%', height: 'calc(100vh - 40px)', position: 'relative' }}>
      <BoundaryApprovalSidebar
        jobs={jobs}
        activeJobId={activeJob?.id || null}
        isLoading={isLoading}
        onRefresh={loadJobs}
        onJobClick={handleJobClick}
        onJobFocus={handleJobFocus}
        onJobVisibilityToggle={handleVisibilityToggle}
        onApprove={handleApprove}
        hasUnsavedEdits={activeJob?.hasUnsavedEdits || false}
        highlightedJobId={highlightedJobId}
      />

      <Box
        sx={{
          flex: 1,
          marginLeft: '350px',
          height: '100%',
          position: 'relative',
        }}
      >
        <CommonMap
          historicalLayersService={historicalLayersService.current}
          type={MAP_TYPES.MAPS}
          buttons={[
            <LayerCheckContainer key="layerCheckContainerButton" customClassName="map-maker-layer-check">
              {Boolean(activeJob) && (defectiveGeometries.length > 0 || warningGeometries.length > 0) && (
                <LayerCustomCheckBox
                  label={`Show Geometry Errors (${defectiveGeometries.length + warningGeometries.length})`}
                  checked={showDefectiveGeometries}
                  onChange={(e) => {
                    setShowDefectiveGeometries(e.target.checked);
                  }}
                />
              )}
              <LayerCustomCheckBox
                label={'Show Timeline'}
                checked={showTimeline}
                onChange={(e) => {
                  setShowTimeline(e.target.checked);
                }}
              />
            </LayerCheckContainer>,
          ]}
          layers={[
            <BoundaryApprovalZonesLayer
              key="boundary-approval-zones"
              jobs={jobs}
              activeJobId={activeJob?.id || null}
              onJobDoubleClick={handleJobFocus}
            />,
            <DefectiveGeometriesLayer
              key="defective-geometries"
              defectiveGeometries={showDefectiveGeometries ? defectiveGeometries : []}
              warningGeometries={showDefectiveGeometries ? warningGeometries : []}
            />,
          ]}
          accessLevel={accessLevel}
          onLoading={onLoading}
          missionActive={missionActive}
          showTimeline={showTimeline}
          drawButtonPopperOffset={[350, 0]}
        />
      </Box>

      <Dialog open={confirmDialogOpen} onClose={handleCancelSwitch}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You might have unsaved changes. Do you really want to load another mission?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancelSwitch}>Cancel</Button>
          <Button onClick={handleConfirmSwitch} color="warning" autoFocus>
            Load
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
